/**
 * Created by Ning Jiang on 2022-06-14.
 */

import {gsap} from "gsap";
import {Layers, SerialLayers} from "../../view/SerialLayers";
import {Serial} from "../../Serial";
import {PickButton} from "./PickButton";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {SerialSound} from "../../sound/SerialSound";
import {BelowStakeWinRestriction} from "../../server/BelowStakeWinRestriction";
import {WinCapText} from "./WinCapText";

export class WinCapView extends PIXI.Container {

    private _animationContainer:PIXI.Container;
    private _buttonContainer:PIXI.Container;
    private _textContainer:WinCapText;
    private _spines:PIXI.spine.Spine[];
    private _buttons:PickButton[][];

    constructor() {
        super();

        this._animationContainer = new PIXI.Container();
        this.addChild(this._animationContainer);

        this._buttons = [];
        this._buttonContainer = new PIXI.Container();
        this.addChild(this._buttonContainer);

        this._textContainer = new WinCapText();
        this.addChild(this._textContainer);

        this._spines = [];
        SerialLayers.getLayer(Layers.SCREENS)?.addChild(this);
    }

    private createButton(level:number, index:number):PickButton {
        const button = new PickButton(level, index);
        this._buttonContainer.addChild(button);
        return button;
    }

    public playWinPresentation(level:number):gsap.core.Timeline {
        if(level === 0) {
            const tl = gsap.timeline();
            tl.add(this.playNextLevelIntro(level));
            tl.add(() => {
                SerialSound.soundPlayer.playTrack({name : SerialSound.WIN_CAP_LEVEL_INTRO[0]});
                this.playText(true);
            }, 3.9333);
            return tl;
        }

        return this.playTransitionAndSpine(level - 1, `level${level}Right`, SerialSound.WIN_CAP_LEVEL_RIGHT[level], false);
    }

    public playNextLevelIntro(level:number):gsap.core.Timeline {
        if(level < 3) {
            return this.playTransitionAndSpine(level, `level${level + 1}Intro`, SerialSound.WIN_CAP_LEVEL_INTRO[level + 1]);
        }
        return gsap.timeline();
    }

    public playAddMoney():void {
        const plus = new PIXI.Sprite(Serial.assetLoader.getTextures("wincap/18700")[0]);
        plus.anchor.set(0.5, 0.5);
        plus.position.set(360, 210);
        SerialLayers.getLayer(Layers.SCREENS)!.addChild(plus);
        const tl = gsap.timeline();
        tl.add(gsap.to(plus, {duration : 0.6, y : 100}), 0.8);
        tl.add(gsap.to(plus, {duration : 0.5, alpha : 0}), 0.9);
        tl.add(() => {
            plus.parent.removeChild(plus);
            plus.destroy();
        });
    }

    private playTransition():gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add([
            () => {SerialLayers.getLayer(Layers.SCREEN_TRANSITION)?.addChild(Serial.background.screenTransition);},
            <gsap.core.Timeline><unknown>Serial.background.screenTransition.getAnimationAutoShowHide()
        ]);
        return tl;
    }

    private prepareSpine(level:number):void {
        if(!this._spines[level]) {
            const animation = new PIXI.spine.Spine(Serial.assetLoader.getSpineAsset(`winCap${level + 1}`));
            animation.autoUpdate = true;
            animation.position.set(360, 360);
            this._spines[level] = animation;
            this._animationContainer.addChild(animation);
            animation.visible = false;
        }
    }

    private showLevel(level:number):void {
        this._animationContainer.children.forEach(child => {
            if(child != this._spines[level]) {
                child.visible = false;
            }
        });
        this._spines[level].visible = true;
    }

    public playTransitionAndSpine(level:number, animationName:string, soundName:string, playTransition:boolean = true):gsap.core.Timeline {
        // Logger.logDev(`play wincap ${animationName}, sound name = ${soundName}`);
        const tl = gsap.timeline();
        this.prepareSpine(level);
        if(playTransition) {
            tl.add(this.playTransition());
        }
        tl.add(() => {
            this._spines[level].state.setAnimation(0, animationName, false);
            if(soundName.length > 0) {
                SerialSound.soundPlayer.playTrack({name : soundName});
            }
            this.showLevel(level);
        });
        tl.add(gsap.to(this, {duration : this._spines[level].spineData.findAnimation(animationName).duration}));
        return tl;
    }

    public playFailPresentation(level:number):gsap.core.Timeline {
        level--;
        const tl = gsap.timeline();
        tl.add(this.playTransitionAndSpine(level, `level${level + 1}EndIntro`, SerialSound.WIN_CAP_LEVEL_WRONG[level + 1], false));
        if(level != 1) {
            tl.add(() => {
                this._spines[level].state.setAnimation(0, `level${level + 1}EndLoop`, true);
            });
        }
        return tl;
    }

    public async showPickLevel(level:number):Promise<{ index:number, replay:boolean }> {
        this.playLevelSound(level);
        this.prepareSpine(level);
        this._spines[level].state.setAnimation(0, `level${level + 1}Loop`, true);
        const levelAmount = BelowStakeWinRestriction.getBasePlayedBetValue() * 18700;
        Serial.screenCountUp.scale.set(1);
        Serial.screenCountUp.getCountUpAnimation(levelAmount * (level + 1), levelAmount * (level + 1), 0.01, SerialLayers.getLayer(Layers.SCREENS)!, 50);
        this.showLevel(level);
        this.playText(false);
        this._textContainer.showSelection();

        if(!this._buttons[level]) {
            this._buttons[level] = [this.createButton(level, 0), this.createButton(level, 1)];
        }

        return new Promise<{ index:number, replay:boolean }>(async resolve => {
            if(SlotGame.apiPlugin.options.replay && Serial.model.parsedGameData.replayNextPlayerInteraction) {
                Logger.logDev(`showPickLevel ${level} - replay = ${SlotGame.apiPlugin.options.replay}`);
                const index = Serial.model.parsedGameData.replayNextPlayerInteraction.selectedIndex;
                this._buttons.forEach((buttons, l) => {
                    const enable = l === level;
                    if(buttons) {
                        buttons[0].visible = enable;
                        buttons[0].enable(false);
                        buttons[1].visible = enable;
                        buttons[1].enable(false);
                    }
                });
                this._buttons[level][0].showAsNormal();
                this._buttons[level][1].showAsNormal();
                const tl = gsap.timeline();
                tl.add(gsap.to(this, {duration : 2}));
                await Promise.resolve(tl);
                resolve({index : index, replay : true});
            } else {
                this._buttons.forEach((buttons, l) => {
                    const enable = l === level;
                    if(buttons) {
                        buttons[0].visible = enable;
                        buttons[0].enable(enable);
                        buttons[1].visible = enable;
                        buttons[1].enable(enable);
                    }
                });

                this._buttons[level][0].addIndexCallback(async () => {
                    resolve({index : 1, replay : false});
                });
                this._buttons[level][1].addIndexCallback(async () => {
                    resolve({index : 2, replay : false});
                });
            }
        });
    }

    private playLevelSound(level:number):void {
        SerialSound.PLAYING_LOOP_EFFECT = "";
        for(let i = 0; i < 3; i++) {
            SerialSound.soundPlayer.stopTrack(SerialSound.WIN_CAP_LOOP[i]);
        }
        SerialSound.soundPlayer.playTrack({name : SerialSound.WIN_CAP_LOOP[level], repeat : 1000});
        SerialSound.PLAYING_LOOP_EFFECT = SerialSound.WIN_CAP_LOOP[level];
    }

    public disablePick():void {
        this._buttons.forEach(buttons => {
            if(buttons) {
                buttons[0].enable(false);
                buttons[1].enable(false);
            }
        });
    }

    public hideAllButtons():void {
        this._buttons.forEach(buttons => {
            if(buttons) {
                buttons[0].visible = false;
                buttons[1].visible = false;
            }
        });
        this._textContainer.hideSelection();
    }

    public playButtonClick(level:number, index:number, replay:boolean):gsap.core.Timeline {
        return this._buttons[level][index - 1].playClick(replay);
    }

    public async wait(level:number):Promise<any> {
        const tl = gsap.timeline();
        if(level < 2) {
            tl.add(() => {
                this._spines[level].state.setAnimation(0, `level${level + 1}Open`, false);
                this.showLevel(level);
            });
            tl.add(() => {}, this._spines[level].spineData.findAnimation(`level${level + 1}Open`).duration);
        }
        tl.add([
            () => {
                SerialLayers.getLayer(Layers.SCREEN_TRANSITION)?.addChild(Serial.background.screenTransition);
                SerialSound.soundPlayer.playTrack({name : SerialSound.WIN_CAP_TRANSITION_LOOP, repeat : 100});
            },
            <gsap.core.Timeline><unknown>Serial.background.screenTransition.getAnimationAutoShowHide(true, false)
        ]);
        tl.add(() => {Serial.background.screenTransition.playLoop()});

        await Promise.resolve(tl);
    }

    public close():void {
        this._spines.forEach(spine => {
            if(spine) {
                spine.parent.removeChild(spine);
                spine.destroy();
            }
        });
        this._spines = [];
        this._buttons.forEach(buttons => {
            if(buttons) {
                this._buttonContainer.removeChild(buttons[0]);
                buttons[0].destroy();
                this._buttonContainer.removeChild(buttons[1]);
                buttons[1].destroy();
            }
        });
        this._buttons = [];
        this._textContainer.remove();
        Serial.screenCountUp.remove();
    }

    public playText(restartTimer:boolean):void {
        this._textContainer.start(restartTimer);
    }
}